import React, { useState, useEffect, useMemo, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import CertificatesPage from "./CertificatesPage";
import VoluntaryCertificatesPage from "./VoluntaryCertificatesPage";
import DeclarationsPage from "./DeclarationsPage";
import DeclarationDetailPage from "./DeclarationDetailPage";
import { DeclarationBasis } from "./types/declaration";
import { isDeclarationBasis } from "./utils/declarationUtils";
import LabExamPage from "./LabExam";
import LabExamDetailPage from "./LabExamDetailPage";
import LabExamDeletedPage from "./LabExamDeletedPage";
import CertificateDetailPage from "./CertificateDetailPage";
import VoluntaryCertificateDetailPage from "./VoluntaryCertificateDetailPage";
import MaterialPage from "./MaterialPage";
import MaterialEmbed from "./MaterialEmbed";
import HomePage from "./HomePage";
import LabDataFormPage from "./AddForm/LabAdd";
import VoluntaryAddFormPage from "./AddForm/VoluntaryAdd";
import MaterialForm from "./AddForm/ActualPrice";
import Header from "./components/Header/Header";
import OrgForm from "./Org_form";
import Examination from "./Examination";
import CertificatesTable from "./CertificatesTable";
import VoluntaryCertificatesTable from "./VoluntaryCertificatesTable";
import LabExamsTable from "./LabExamsTable";
import Auth from "./Auth";
import { UserProvider } from "./contexts/UserContext";
import { useUser } from "./contexts/UserContext";
import DynamicsOSPage from "./pages/DynamicsOSPage";
import DynamicsLabsPage from "./pages/DynamicsLabsPage";
import DynamicsApplicantsPage from "./pages/DynamicsApplicantsPage";
import DynamicsManufacturersPage from "./pages/DynamicsManufacturersPage";
import CertificatesFilter from "./components/Filters/CertificatesFilter";
import axios from "axios";
import { getApiV1Url } from "./config/api";
import { Certif } from "./CertificatesPage";
import FeedbackForm from "./FeedbackForm";
import LettersPage from "./LetterGraphFlowPage";
import RosakkreditatsiyaOSPage from "./pages/RosakkreditatsiyaOSPage";
import RosakkreditatsiyaILPage from "./pages/RosakkreditatsiyaILPage";

function isTokenValid(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const access = localStorage.getItem('access');
  if (!access || !isTokenValid(access)) {
    return <Navigate to="/auth" replace />;
  }
  return <>{children}</>;
}

function EditorRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useUser();
  const access = localStorage.getItem('access');
  if (!access || !isTokenValid(access)) {
    return <Navigate to="/auth" replace />;
  }
  if (loading) {
    return null;
  }
  const isEditor = !!user?.groups?.some((g) => g.name === 'Редактор');
  if (!isEditor) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function CertificatesPageWithHeader() {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  return (
    <>
      <Header onFilterClick={() => setIsFilterOpen(true)} />
      <CertificatesPage isFilterOpen={isFilterOpen} setIsFilterOpen={setIsFilterOpen} />
    </>
  );
}

function LabExamPageWithHeader({ weldedJointsOnly = false }: { weldedJointsOnly?: boolean } = {}) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  return (
    <>
      <Header onFilterClick={() => setIsFilterOpen(true)} />
      <LabExamPage isFilterOpen={isFilterOpen} setIsFilterOpen={setIsFilterOpen} weldedJointsOnly={weldedJointsOnly} />
    </>
  );
}

function LabExamDetailPageWithHeader() {
  return (
    <>
      <Header />
      <LabExamDetailPage />
    </>
  );
}

function LabExamDeletedPageWithHeader() {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  return (
    <>
      <Header onFilterClick={() => setIsFilterOpen(true)} />
      <LabExamDeletedPage isFilterOpen={isFilterOpen} setIsFilterOpen={setIsFilterOpen} />
    </>
  );
}

function CertificateDetailPageWithHeader() {
  return (
    <>
      <Header />
      <CertificateDetailPage />
    </>
  );
}

function VoluntaryCertificateDetailPageWithHeader() {
  return (
    <>
      <Header />
      <VoluntaryCertificateDetailPage />
    </>
  );
}

function VoluntaryCertificatesPageWithHeader() {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  return (
    <>
      <Header onFilterClick={() => setIsFilterOpen(true)} />
      <VoluntaryCertificatesPage isFilterOpen={isFilterOpen} setIsFilterOpen={setIsFilterOpen} />
    </>
  );
}

function DeclarationsPageWithHeader() {
  const { basis } = useParams<{ basis: string }>();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  if (!isDeclarationBasis(basis)) {
    return <Navigate to="/" replace />;
  }
  return (
    <>
      <Header onFilterClick={() => setIsFilterOpen(true)} />
      {/* key сбрасывает фильтры при переходе между ПП 2425 и ПП 353 */}
      <DeclarationsPage key={basis} basis={basis as DeclarationBasis} isFilterOpen={isFilterOpen} setIsFilterOpen={setIsFilterOpen} />
    </>
  );
}

function DeclarationDetailPageWithHeader() {
  const { basis } = useParams<{ basis: string }>();
  if (!isDeclarationBasis(basis)) {
    return <Navigate to="/" replace />;
  }
  return (
    <>
      <Header />
      <DeclarationDetailPage />
    </>
  );
}

const FILTER_STORAGE_KEY = 'certificates_dynamics_filters';

interface FilterState {
  filters: {
    cert_reg_number: string[];
    os_name: string[];
    os_number: string[];
    applicant_shortname: string[];
    manufacture_inn: string[];
    cert_reg_data: string[];
    manufacture_fullname: string[];
    applicant_inn: string[];
    applicant_region: string[];
    region_manufacturer: string[];
    lab_names: string[];
  };
  filteredAptsComments: string[];
  filteredCertStatuses: string[];
  filteredYears: string[];
  dateRange: { start: string; end: string };
  endDateRange: { start: string; end: string };
  showTerminatedOnly: boolean;
}

const getDefaultFilters = (): FilterState => ({
  filters: {
    cert_reg_number: [],
    os_name: [],
    os_number: [],
    applicant_shortname: [],
    manufacture_inn: [],
    cert_reg_data: [],
    manufacture_fullname: [],
    applicant_inn: [],
    applicant_region: [],
    region_manufacturer: [],
    lab_names: []
  },
  filteredAptsComments: [],
  filteredCertStatuses: [],
  filteredYears: [],
  dateRange: { start: '', end: '' },
  endDateRange: { start: '', end: '' },
  showTerminatedOnly: false
});

function DynamicsPageWrapper({ PageComponent }: { PageComponent: React.ComponentType<{ filteredCertif?: Certif[] }> }) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [certif, setCertif] = useState<Certif[]>([]);
  const [loading, setLoading] = useState(true);

  const loadFiltersFromStorage = (): FilterState => {
    const base = getDefaultFilters();
    try {
      const stored = localStorage.getItem(FILTER_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) || {};
        const ensureArray = (v: any) => (Array.isArray(v) ? v : []);
        const merged: FilterState = {
          filters: {
            cert_reg_number: ensureArray(parsed.filters?.cert_reg_number),
            os_name: ensureArray(parsed.filters?.os_name),
            os_number: ensureArray(parsed.filters?.os_number),
            applicant_shortname: ensureArray(parsed.filters?.applicant_shortname),
            manufacture_inn: ensureArray(parsed.filters?.manufacture_inn),
            cert_reg_data: ensureArray(parsed.filters?.cert_reg_data),
            manufacture_fullname: ensureArray(parsed.filters?.manufacture_fullname),
            applicant_inn: ensureArray(parsed.filters?.applicant_inn),
            applicant_region: ensureArray(parsed.filters?.applicant_region),
            region_manufacturer: ensureArray(parsed.filters?.region_manufacturer),
            lab_names: ensureArray(parsed.filters?.lab_names),
          },
          filteredAptsComments: ensureArray(parsed.filteredAptsComments),
          filteredCertStatuses: ensureArray(parsed.filteredCertStatuses),
          filteredYears: ensureArray(parsed.filteredYears),
          dateRange: {
            start: parsed.dateRange?.start || '',
            end: parsed.dateRange?.end || '',
          },
          endDateRange: {
            start: parsed.endDateRange?.start || '',
            end: parsed.endDateRange?.end || '',
          },
          showTerminatedOnly: Boolean(parsed.showTerminatedOnly),
        };
        return merged;
      }
    } catch (e) {
      console.error('Error loading filters from storage:', e);
    }
    return base;
  };

  const defaultFilters = loadFiltersFromStorage();

  const [filters, setFilters] = useState(defaultFilters.filters);
  const [filteredAptsComments, setFilteredAptsComments] = useState<string[]>(defaultFilters.filteredAptsComments);
  const [filteredCertStatuses, setFilteredCertStatuses] = useState<string[]>(defaultFilters.filteredCertStatuses);
  const [filteredYears, setFilteredYears] = useState<string[]>(defaultFilters.filteredYears);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>(defaultFilters.dateRange);
  const [endDateRange, setEndDateRange] = useState<{ start: string; end: string }>(defaultFilters.endDateRange);
  const [showTerminatedOnly, setShowTerminatedOnly] = useState(defaultFilters.showTerminatedOnly);

  const saveFiltersToStorage = useCallback(() => {
    try {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify({
        filters,
        filteredAptsComments,
        filteredCertStatuses,
        filteredYears,
        dateRange,
        endDateRange,
        showTerminatedOnly
      }));
    } catch (e) {
      console.error('Error saving filters to storage:', e);
    }
  }, [filters, filteredAptsComments, filteredCertStatuses, filteredYears, dateRange, endDateRange, showTerminatedOnly]);

  useEffect(() => {
    saveFiltersToStorage();
  }, [saveFiltersToStorage]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const access = localStorage.getItem('access');
        const response = await axios.get<Certif[]>(getApiV1Url('CERTIFICATES'), {
          headers: { Authorization: `Bearer ${access}` },
        });
        setCertif(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching certificates:', error);
        setCertif([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (isFilterOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFilterOpen) {
        setIsFilterOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isFilterOpen]);

  const filteredCertif = useMemo(() => {
    if (!certif.length) return [];

    const normalizeText = (text: string) => text?.toLowerCase().trim() || '';

    let filteredData = [...certif];

    if (dateRange.start || dateRange.end) {
      filteredData = filteredData.filter(item => {
        if (!item.cert_reg_data) return false;
        const itemDate = new Date(item.cert_reg_data);
        if (dateRange.start) {
          const startDate = new Date(dateRange.start);
          if (itemDate < startDate) return false;
        }
        if (dateRange.end) {
          const endDate = new Date(dateRange.end);
          if (itemDate > endDate) return false;
        }
        return true;
      });
    }

    if (endDateRange.start || endDateRange.end) {
      filteredData = filteredData.filter(item => {
        if (!item.cert_finish_data) return false;
        const itemDate = new Date(item.cert_finish_data);
        if (endDateRange.start) {
          const startDate = new Date(endDateRange.start);
          if (itemDate < startDate) return false;
        }
        if (endDateRange.end) {
          const endDate = new Date(endDateRange.end);
          if (itemDate > endDate) return false;
        }
        return true;
      });
    }

    if (showTerminatedOnly) {
      filteredData = filteredData.filter(item => {
        return item.cert_stop_data && item.cert_stop_data.trim() !== '';
      });
    }

    if (filteredYears.length > 0) {
      filteredData = filteredData.filter(item =>
        item.cert_reg_data && filteredYears.includes(item.cert_reg_data.slice(0, 4))
      );
    }

    filteredData = filteredData.filter(cert => {
      if (filteredAptsComments.length > 0) {
        const commentMatches = filteredAptsComments.some(comment =>
          normalizeText(cert.comment_apts) === normalizeText(comment)
        );
        if (!commentMatches) return false;
      }

      if (filteredCertStatuses.length > 0) {
        const statusMatches = filteredCertStatuses.some(status =>
          normalizeText(cert.cert_status) === normalizeText(status)
        );
        if (!statusMatches) return false;
      }

      if (filters.lab_names.length > 0) {
        const labFields = ['lan_name_1', 'lan_name_2', 'lan_name_3'];
        const labMatches = filters.lab_names.some(filterLabName => {
          return labFields.some(field => {
            const labName = (cert as any)[field];
            return labName && labName !== 'Отсутствует' && normalizeText(labName).includes(normalizeText(filterLabName));
          });
        });
        if (!labMatches) return false;
      }

      for (const [key, values] of Object.entries(filters || {})) {
        if (key === 'lab_names') continue;
        const vals = Array.isArray(values) ? values : [];
        if (vals.length > 0) {
          const fieldValue = (cert as any)[key];
          const fieldMatches = vals.some(value =>
            normalizeText(fieldValue).includes(normalizeText(value))
          );
          if (!fieldMatches) return false;
        }
      }

      return true;
    });

    return filteredData;
  }, [certif, filters, filteredAptsComments, filteredCertStatuses, filteredYears, dateRange, endDateRange, showTerminatedOnly]);

  const handleFilterChange = useCallback((newFilters: typeof filters) => {
    setFilters(newFilters);
  }, []);

  const handleChartFilterChange = useCallback((newFilters: {
    aptsComments: string[];
    certStatuses: string[];
    years: string[];
  }) => {
    setFilteredAptsComments(newFilters.aptsComments);
    setFilteredCertStatuses(newFilters.certStatuses);
    setFilteredYears(newFilters.years);
  }, []);

  const handleDateRangeChange = useCallback((field: 'cert_reg_data' | 'cert_finish_data', startDate: string, endDate: string) => {
    if (field === 'cert_reg_data') {
      setDateRange({ start: startDate, end: endDate });
    } else if (field === 'cert_finish_data') {
      setEndDateRange({ start: startDate, end: endDate });
    }
  }, []);

  const handleTerminatedFilterChange = useCallback((showTerminatedOnly: boolean) => {
    setShowTerminatedOnly(showTerminatedOnly);
  }, []);

  const handleResetFilters = useCallback(() => {
    const emptyFilters = {
      cert_reg_number: [] as string[],
      os_name: [] as string[],
      os_number: [] as string[],
      applicant_shortname: [] as string[],
      manufacture_inn: [] as string[],
      cert_reg_data: [] as string[],
      manufacture_fullname: [] as string[],
      applicant_inn: [] as string[],
      applicant_region: [] as string[],
      region_manufacturer: [] as string[],
      lab_names: [] as string[]
    };
    setFilters(emptyFilters);
    setFilteredAptsComments([]);
    setFilteredCertStatuses([]);
    setFilteredYears([]);
    setDateRange({ start: '', end: '' });
    setEndDateRange({ start: '', end: '' });
    setShowTerminatedOnly(false);
    localStorage.removeItem(FILTER_STORAGE_KEY);
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        Загрузка...
      </div>
    );
  }

  return (
    <>
      <Header onFilterClick={() => setIsFilterOpen(true)} />
      {isFilterOpen && (
        <div className="certificates-filter-modal-overlay">
          <div className="certificates-filter-modal" style={{ overflowY: 'visible' }}>
            <button className="certificates-filter-modal__close" onClick={() => setIsFilterOpen(false)}>
              ×
            </button>
            <CertificatesFilter
              certificates={certif}
              onFilterChange={handleFilterChange}
              chartFilters={{
                aptsComments: filteredAptsComments,
                certStatuses: filteredCertStatuses,
                years: filteredYears
              }}
              onChartFilterChange={handleChartFilterChange}
              onDateRangeChange={handleDateRangeChange}
              onTerminatedFilterChange={handleTerminatedFilterChange}
              initialFilters={filters}
              initialDateRanges={{ cert_reg_data: dateRange, cert_finish_data: endDateRange }}
            />
          </div>
        </div>
      )}
      <PageComponent filteredCertif={filteredCertif} />
    </>
  );
}

function TitleSetter() {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const path = location.pathname;
    let title = "АПТС";

    switch (path) {
      case "/":
        title = "Главная страница";
        break;
      case "/certificates":
        title = "Обязательные сертификаты";
        break;
      case "/voluntary-certificates":
        title = "Добровольные сертификаты";
        break;
      case "/declarations/2425":
        title = "Декларации ПП 2425";
        break;
      case "/declarations/353":
        title = "Декларации ПП 353";
        break;
      case "/lab-exam":
        title = "Лабораторные испытания";
        break;
      case "/welded-joint-exams":
        title = "Реестр испытаний сварных соединений";
        break;
      case "/lab-exam/deleted":
        title = "Удаленные лабораторные испытания";
        break;
      case "/embed/certificates-table":
        title = "Аналитический центр АПТС";
        break;
      case "/embed/voluntary-certificates-table":
        title = "Аналитический центр АПТС";
        break;
      case "/embed/lab-exams-table":
      case "/embed/welded-joint-exams":
      case "/embed/welded_joint_exams":
        title = "Аналитический центр АПТС";
        break;
      case "/material": {
        const type = params.get("type");
        if (type === "pe") title = "Полиэтилен — Цены на полимерное сырье";
        else if (type === "ppr") title = "Полипропилен — Цены на полимерное сырье";
        else if (type === "pvh") title = "Поливинилхлорид — Цены на полимерное сырье";
        else title = "Цены на полимерное сырье";
        break;
      }
      case "/lab-add":
        title = "Добавить лабораторные данные";
        break;
      case "/material-price":
        title = "Управление ценами материалов";
        break;
      case "/voluntary-add":
        title = "Добавление добровольных сертификатов";
        break;
      case "/org-form":
        title = "Комплексная проверка организации";
        break;
      case "/examination":
        title = "Комплексная проверка организации";
        break;
      case "/auth":
        title = "Авторизация";
        break;
      case "/certificates/dynamics/os":
        title = "Органы по сертификации - Динамика";
        break;
      case "/certificates/dynamics/labs":
        title = "Лаборатории - Динамика";
        break;
      case "/certificates/dynamics/applicants":
        title = "Заявители - Динамика";
        break;
      case "/certificates/dynamics/manufacturers":
        title = "Изготовители - Динамика";
        break;
      case "/letters":
        title = "Рестр зарегистрированных писем";
        break;
      case "/rosakkreditatsiya/os":
        title = "Органы по сертификации — Росаккредитация";
        break;
      case "/rosakkreditatsiya/il":
        title = "Испытательные лаборатории — Росаккредитация";
        break;
      default:
        title = "АПТС";
    }

    if (path.startsWith("/form/")) {
      title = "Обратная связь";
    }

    if (path.startsWith("/voluntary-edit/")) {
      title = "Редактирование добровольного сертификата";
    }

    document.title = `${title} — АПТС`;

    // Устанавливаем description только для страницы писем
    if (path === "/letters") {
      const descText = "Граф реестра официальной кореспонденции";
      let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'description';
        document.head.appendChild(meta);
      }
      meta.content = descText;
    }
  }, [location]);

  return null;
}

function App() {
  return (
    <UserProvider>
      <BrowserRouter>
        <TitleSetter />
        <Routes>
          <Route path="/form/:dealId" element={<FeedbackForm />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/embed/material" element={<MaterialEmbed />} />
          <Route path="/embed/material/:type" element={<MaterialEmbed />} />
          <Route path="/embed/voluntary-certificates-table" element={<VoluntaryCertificatesTable />} />
          <Route path="/embed/certificates-table" element={<CertificatesTable />} />
          <Route path="/embed/lab-exams-table" element={<LabExamsTable />} />
          <Route path="/embed/welded-joint-exams" element={<LabExamsTable weldedJointsOnly />} />
          <Route path="/embed/welded_joint_exams" element={<LabExamsTable weldedJointsOnly />} />
          <Route path="/" element={
            <PrivateRoute>
              <><Header /><HomePage /></>
            </PrivateRoute>
          } />
          <Route path="/letters" element={<PrivateRoute><LettersPage /></PrivateRoute>} />
          <Route path="/certificates" element={<PrivateRoute><CertificatesPageWithHeader /></PrivateRoute>} />
          <Route path="/certificates/:certNumber" element={<PrivateRoute><CertificateDetailPageWithHeader /></PrivateRoute>} />
          <Route path="/voluntary-certificates" element={<PrivateRoute><VoluntaryCertificatesPageWithHeader /></PrivateRoute>} />
          <Route path="/voluntary-certificates/:certNumber" element={<PrivateRoute><VoluntaryCertificateDetailPageWithHeader /></PrivateRoute>} />
          <Route path="/declarations/:basis" element={<PrivateRoute><DeclarationsPageWithHeader /></PrivateRoute>} />
          <Route path="/declarations/:basis/:declNumber" element={<PrivateRoute><DeclarationDetailPageWithHeader /></PrivateRoute>} />
          <Route path="/lab-exam" element={<PrivateRoute><LabExamPageWithHeader /></PrivateRoute>} />
          <Route path="/welded-joint-exams" element={<PrivateRoute><LabExamPageWithHeader weldedJointsOnly /></PrivateRoute>} />
          <Route path="/lab-exam/:examId" element={<PrivateRoute><LabExamDetailPageWithHeader /></PrivateRoute>} />
          <Route path="/lab-exam/deleted" element={<EditorRoute><LabExamDeletedPageWithHeader /></EditorRoute>} />
          <Route path="/material" element={<PrivateRoute><><Header /><MaterialPage /></></PrivateRoute>} />
          <Route path="/voluntary-certificates-table" element={<PrivateRoute><><Header /><VoluntaryCertificatesTable /></></PrivateRoute>} />
          <Route path="/certificates-table" element={<PrivateRoute><><Header /><CertificatesTable /></></PrivateRoute>} />
          <Route path="/lab-exams-table" element={<PrivateRoute><><Header /><LabExamsTable /></></PrivateRoute>} />
          <Route path="/welded-joint-exams-table" element={<PrivateRoute><><Header /><LabExamsTable weldedJointsOnly /></></PrivateRoute>} />
          <Route path="/lab-add" element={<EditorRoute><><Header /><LabDataFormPage /></></EditorRoute>} />
          <Route path="/voluntary-add" element={<EditorRoute><><Header /><VoluntaryAddFormPage /></></EditorRoute>} />
          <Route path="/voluntary-edit/:id" element={<EditorRoute><><Header /><VoluntaryAddFormPage /></></EditorRoute>} />
          <Route path="/material-price" element={<PrivateRoute><><Header /><MaterialForm /></></PrivateRoute>} />
          <Route path="/org-form" element={<PrivateRoute><OrgForm /></PrivateRoute>} />
          <Route path="/examination" element={<PrivateRoute><Examination /></PrivateRoute>} />
          <Route path="/certificates/dynamics/os" element={<PrivateRoute><DynamicsPageWrapper PageComponent={DynamicsOSPage} /></PrivateRoute>} />
          <Route path="/certificates/dynamics/labs" element={<PrivateRoute><DynamicsPageWrapper PageComponent={DynamicsLabsPage} /></PrivateRoute>} />
          <Route path="/certificates/dynamics/applicants" element={<PrivateRoute><DynamicsPageWrapper PageComponent={DynamicsApplicantsPage} /></PrivateRoute>} />
          <Route path="/certificates/dynamics/manufacturers" element={<PrivateRoute><DynamicsPageWrapper PageComponent={DynamicsManufacturersPage} /></PrivateRoute>} />
          <Route path="/rosakkreditatsiya/os" element={<PrivateRoute><RosakkreditatsiyaOSPage /></PrivateRoute>} />
          <Route path="/rosakkreditatsiya/il" element={<PrivateRoute><RosakkreditatsiyaILPage /></PrivateRoute>} />
        </Routes>
      </BrowserRouter>
    </UserProvider>
  );
}

export default App;
